import { Component, OnInit, Input } from '@angular/core'
import {
  Channel,
  ConnectedChannelAccountResponse,
  DisconnectChannelComplete,
  removeRateMappingForChannel,
  Destroyable,
  untilDestroy,
  InfoDialogService,
  epochToView,
} from '@tokeet-frontend/tv3-platform'
import { switchMap, take, tap } from 'rxjs/operators'
import { Store, select } from '@ngrx/store'
import * as R from 'ramda'
import {
  RefreshConnectionsForChannelComplete,
  RemoveLocalConnectionsByIds,
} from '@tv3/store/connection/connection.actions'
import { Actions, ofType } from '@ngrx/effects'
import { selectConnectionsByChannelId } from '@tv3/store/connection/connection.selectors'
import { Connection } from '@tv3/store/connection/connection.model'
import { BehaviorSubject, Observable } from 'rxjs'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { ChannelHelperService } from '@tv3/containers/channels/channel-connect/channel-helper.service'
import { ChannelConnectWizardsService } from '@tv3/containers/channels/channel-connect/channel-connect-wizards/index.service'
import { ChannelAccountsCacheService } from '../channel-accounts-cache.service'

@Component({
  selector: 'app-channel-accounts',
  templateUrl: './channel-accounts.component.html',
  styleUrls: ['./channel-accounts.component.scss'],
})
export class ChannelAccountsComponent extends Destroyable implements OnInit {
  @Input() channel: Channel

  tableType = TableType

  isLoading = true
  accounts$: Observable<any[]>

  refresh$ = new BehaviorSubject(true)

  constructor(
    private store: Store<any>,
    private actions: Actions,
    private infoDialog: InfoDialogService,
    private channelHelperService: ChannelHelperService,
    private channelConnectWizards: ChannelConnectWizardsService,
    private accountsCache: ChannelAccountsCacheService
  ) {
    super()
  }

  ngOnInit(): void {
    this.accounts$ = this.refresh$.pipe(
      switchMap(() => this.accountsCache.getAccounts(this.channel)),
      tap(() => (this.isLoading = false))
    )

    this.actions
      .pipe(
        ofType(DisconnectChannelComplete.type),
        tap(({ channelId }) => {
          this.accountsCache.invalidate(this.channel)
          this.refresh$.next(true)
          // remove rate mappings for this channel
          this.store.dispatch(removeRateMappingForChannel({ channelId: channelId }))
        }),
        switchMap(({ channelId }) => this.store.pipe(select(selectConnectionsByChannelId, { id: channelId }), take(1))),
        untilDestroy(this)
      )
      .subscribe((connections: Connection[]) => {
        // remove local connection data
        this.store.dispatch(RemoveLocalConnectionsByIds({ connectionIds: R.map((c) => c.id, connections) }))
      })

    this.actions.pipe(ofType(RefreshConnectionsForChannelComplete), untilDestroy(this)).subscribe(() => {
      this.accountsCache.invalidate(this.channel)
      this.refresh$.next(true)
    })
  }

  onDisconnect(channelAccount: ConnectedChannelAccountResponse) {
    this.channelHelperService.disconnectAccount(this.channel, channelAccount).subscribe(() => {
      this.accountsCache.invalidate(this.channel)
      this.refresh$.next(true)
    })
  }

  onReconnect(channelAccount: ConnectedChannelAccountResponse) {
    this.channelConnectWizards.connect(this.channel).subscribe(() => {
      this.accountsCache.invalidate(this.channel)
      this.refresh$.next(true)
    })
  }

  onDetails(account) {
    const user = account.user_info

    const items = [
      ['Account', account.propertyId],
      ['Connected At', epochToView(account.createdOn || account.connectedAt)],
      // ['Hotel Name', lodash.get(account, 'rooms.0.hotelName', '...')],
    ]
    if (user) {
      items.push(['User', `${user.first_name || ''} ${user.last_name || ''}`.trim()])
    }

    const content = items.map((item) => `<div><b>${item[0]}:</b> ${item[1]}</div>`).join('\n')

    this.infoDialog.open({
      title: `Account #${account.propertyId}`,
      body: content,
    })
  }
}
